// AddRole.jsx
import {
    Cancel as CancelIcon,
    Home as HomeIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Grid,
    Link,
    Paper,
    Snackbar,
    TextField,
    Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { addRole, resetCurrentRole } from '../../redux/role/roleAction';

const AddRole = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  // State
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  // Redux state
  const addRoleState = useSelector((state) => state?.role?.addRole || {});

  // Available modules
  const modules = [
    { id: 1, name: 'adminDashboard', label: 'Admin Dashboard' },
    { id: 2, name: 'dashboard', label: 'Dashboard' },
    { id: 3, name: 'patient', label: 'Patient Management' },
    { id: 4, name: 'admissions', label: 'Admissions' },
    { id: 5, name: 'doctor', label: 'Doctor Management' },
    { id: 6, name: 'securityOfficer', label: 'Security Officer' },
    { id: 7, name: 'staff', label: 'Staff Management' },
    { id: 8, name: 'block', label: 'Block Management' },
    { id: 9, name: 'floor', label: 'Floor Management' },
    { id: 10, name: 'ward', label: 'Ward Management' },
    { id: 11, name: 'department', label: 'Department' },
    { id: 12, name: 'specialization', label: 'Specialization' },
    { id: 13, name: 'patientTransfer', label: 'Patient Transfer' },
    { id: 14, name: 'adminTransfer', label: 'Admin Transfer' },
    { id: 15, name: 'history', label: 'History' },
    { id: 16, name: 'rolePermission', label: 'Role Permission' },
    { id: 17, name: 'adminProfile', label: 'Admin Profile' },
    { id: 18, name: 'staffProfile', label: 'Staff Profile' },
    { id: 19, name: 'doctorProfile', label: 'Doctor Profile' },
    { id: 20, name: 'staffPatientList', label: 'Staff Patient List' },
    { id: 21, name: 'report', label: 'Reports' },
  ];

  // Form setup
  const { control, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm({
    defaultValues: {
      roleName: '',
      modules: {}
    }
  });

  // Initialize module permissions
  useEffect(() => {
    const initialPermissions = {};
    modules.forEach(module => {
      initialPermissions[module.id] = {
        add: false,
        edit: false,
        view: false,
        delete: false,
        allAccess: false,
        noAccess: true
      };
    });
    setValue('modules', initialPermissions);
  }, [setValue]);

  // Handle add role response
  useEffect(() => {
    if (addRoleState.success) {
      setSnackbarMessage('Role added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
      setTimeout(() => {
        navigate('/role-permissions');
      }, 2000);
    } else if (addRoleState.error) {
      if(addRoleState?.errorMessage === "Request failed with status code 400"){
        setSnackbarMessage('Failed to add role: Role already exists');
      } else {
        setSnackbarMessage(`Failed to add role: ${addRoleState.error}`);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      dispatch(resetCurrentRole());
    }
  }, [addRoleState, dispatch, navigate]);

  const handlePermissionChange = (moduleId, permission, value) => {
    const currentValues = getValues(`modules.${moduleId}`);
    const newState = { ...currentValues };
    newState[permission] = value;
    
    if (permission === 'allAccess' && value === true) {
      newState.add = true;
      newState.edit = true;
      newState.view = true;
      newState.delete = true;
      newState.allAccess = true;
      newState.noAccess = false;
    } else if (permission === 'noAccess' && value === true) {
      newState.add = false;
      newState.edit = false;
      newState.view = false;
      newState.delete = false;
      newState.allAccess = false;
      newState.noAccess = true;
    } else if (['add', 'edit', 'view', 'delete'].includes(permission)) {
      newState.allAccess = 
        newState.add && 
        newState.edit && 
        newState.view && 
        newState.delete;
      newState.noAccess = 
        !newState.add && 
        !newState.edit && 
        !newState.view && 
        !newState.delete;
    }
    
    Object.keys(newState).forEach(key => {
      setValue(`modules.${moduleId}.${key}`, newState[key]);
    });
  };

  const onSubmit = (data) => {
    const permissionsObject = {};
    modules.forEach(module => {
      const modulePermission = data.modules[module.id] || {};
      let apiModuleName = module.name;
      
      // Handle special API naming
      if (module.name === 'rolePermission') {
        apiModuleName = 'RolePerminsions';
      }
      
      permissionsObject[apiModuleName] = {
        add: modulePermission.add || false,
        edit: modulePermission.edit || false,
        view: modulePermission.view || false,
        delete: modulePermission.delete || false,
        allaccess: modulePermission.allAccess || false,
        noaccess: modulePermission.noAccess || false
      };
    });
    
    const rolePayload = {
      name: data.roleName,
      status: "1",
      permissions: permissionsObject
    };
    
    dispatch(addRole([rolePayload]));
  };

  const handleCancel = () => {
    navigate('/role-permissions');
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          underline="hover" 
          color="inherit" 
          href="/role-permissions"
          onClick={(e) => {
            e.preventDefault();
            navigate('/role-permissions');
          }}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Role Permissions
        </Link>
        <Typography color="text.primary">Add Role</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h4" fontWeight="bold" color="primary">
          Add New Role
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
          Create a new role and configure module permissions
        </Typography>
      </Paper>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          {/* Role Name */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Controller
                name="roleName"
                control={control}
                rules={{ required: "Role name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Role Name"
                    fullWidth
                    required
                    error={!!errors.roleName}
                    helperText={errors.roleName?.message}
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Permissions */}
        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Module Permissions
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {modules.map(module => (
              <Grid item xs={12} key={module.id}>
                <Paper 
                  elevation={1} 
                  sx={{ 
                    p: 2, 
                    borderRadius: 2,
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {module.label}
                  </Typography>
                  
                  <Grid container spacing={2}>
                    {['view', 'add', 'edit', 'delete', 'allAccess', 'noAccess'].map((permission) => {
                      const labels = {
                        'add': 'Add',
                        'edit': 'Edit',
                        'view': 'View',
                        'delete': 'Delete',
                        'allAccess': 'All Access',
                        'noAccess': 'No Access'
                      };
                      
                      return (
                        <Grid item xs={6} sm={4} md={2} key={permission}>
                          <Controller
                            name={`modules.${module.id}.${permission}`}
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={field.value || false}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked);
                                      handlePermissionChange(module.id, permission, e.target.checked);
                                    }}
                                    color={permission === 'allAccess' ? 'success' : 
                                           permission === 'noAccess' ? 'error' : 'primary'}
                                  />
                                }
                                label={labels[permission]}
                              />
                            )}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Actions */}
        <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              size="large"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<SaveIcon />}
              size="large"
              sx={{ 
                backgroundColor: '#4CAF50',
                '&:hover': { backgroundColor: '#45a049' }
              }}
            >
              Save Role
            </Button>
          </Box>
        </Paper>
      </form>

      {/* Snackbar */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbarSeverity} 
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddRole;
